import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  FormEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { useRouter, useLocation } from '@tanstack/react-router';
import clsx from 'clsx';
import { t } from '@lingui/core/macro';
import InstapaperPremiumModal from './InstapaperPremiumModal';
import Icon from './ui/Icon';
import Tooltip from './ui/Tooltip';
import { ButtonIconTooltipContent } from './ui/ButtonIcon';

interface CollapsibleSearchBarProps {
  isSubscriber?: boolean;
  initialQuery?: string;
  isBatchMode?: boolean;
  /**
   * When true (3-column layout), the expanded field stretches to fill the
   * header: its left edge lands on the header's content-left instead of using
   * the fixed collapsed-vs-expanded width. The width is measured so it tracks
   * the resizable list-column width live. Off (2-column / full / other pages)
   * keeps the fixed expanded width.
   */
  fillHeaderWidth?: boolean;
}

function CollapsibleSearchBar({
  isSubscriber = false,
  initialQuery = '',
  isBatchMode = false,
  fillHeaderWidth = false,
}: CollapsibleSearchBarProps) {
  const router = useRouter();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Measured pixel width for the "fill the header" mode; null falls back to the
  // fixed width class.
  const [filledWidth, setFilledWidth] = useState<number | null>(null);

  // Get the search query from the URL if we're on the search page
  // location.search is a typed object from TanStack Router, so we access the q property directly
  const urlQuery =
    location.pathname === '/search' &&
    location.search &&
    typeof location.search === 'object' &&
    'q' in location.search
      ? (location.search.q as string) || ''
      : '';
  const currentQuery = urlQuery || initialQuery || '';

  const [query, setQuery] = useState(currentQuery);
  // Initialize expanded state based on the current query. The search page shows
  // the active term in its own header (the "Search" section), so the bar starts
  // collapsed there and only expands when the user reaches for it to refine.
  const [isExpanded, setIsExpanded] = useState(
    !!currentQuery && location.pathname !== '/search',
  );
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // The `!query.trim()` guard already keeps the bar open while the user has text,
  // so the search page needs no special-casing here — it collapses like anywhere
  // else once empty (e.g. after submitting, which clears focus).
  const isAllowedToCollapse = isExpanded && !query.trim();

  // Escape is an explicit dismissal, so it collapses whenever the bar is
  // expanded — even with an unsubmitted query — except on the search results
  // page, where the expanded bar reflects the active search.
  const canCollapseOnEscape = isExpanded && location.pathname !== '/search';

  // Update query when route changes
  useEffect(() => {
    if (
      location.pathname === '/search' &&
      location.search &&
      typeof location.search === 'object' &&
      'q' in location.search
    ) {
      // Keep the input value in sync with the URL so re-expanding to refine a
      // search pre-fills the current term. Don't force-expand: the search page
      // surfaces the term in its own header instead.
      const searchQuery = (location.search.q as string) || '';
      setQuery(searchQuery);
    }
  }, [location]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Handle click outside to collapse
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't collapse if clicking inside the search container or if there's a query
      if (!target.closest('[data-search-container]') && isAllowedToCollapse) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAllowedToCollapse]);

  // Handle escape key to collapse
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && canCollapseOnEscape) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canCollapseOnEscape]);

  // "/" opens search from anywhere, matching common web apps (GitHub, Slack).
  // Ignored while typing in a field (so "/" types literally) or while batch mode
  // disables search. Non-subscribers get the same upsell as clicking the icon.
  useEffect(() => {
    const handleSlashKey = (event: KeyboardEvent) => {
      if (
        event.key !== '/' ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isBatchMode
      ) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        // eslint-disable-next-line lingui/no-unlocalized-strings -- CSS selector, not user text
        target?.matches?.('input, textarea, select, [contenteditable="true"]') ||
        target?.closest?.('[contenteditable="true"]')
      ) {
        return;
      }
      event.preventDefault();
      if (!isSubscriber) {
        setShowPremiumModal(true);
        return;
      }
      if (isExpanded) {
        inputRef.current?.focus();
      } else {
        setIsExpanded(true); // the focus effect focuses the input once expanded
      }
    };

    document.addEventListener('keydown', handleSlashKey);
    return () => document.removeEventListener('keydown', handleSlashKey);
  }, [isBatchMode, isExpanded, isSubscriber]);

  // In fill-header mode, measure the distance from the header's content-left to
  // the search icon so the expanded field spans the whole header. The field is
  // anchored at its right edge (right-[-10px]) and grows left, so left edge =
  // rightEdge - width. Recompute on header resize (list-column drag) so the
  // width tracks the column live. useLayoutEffect avoids a one-frame flash of
  // the fixed width before the measured width applies.
  useLayoutEffect(() => {
    if (!fillHeaderWidth || !isExpanded) {
      setFilledWidth(null);
      return;
    }

    const containerEl = containerRef.current;
    const headerEl = containerEl?.closest<HTMLElement>(
      // eslint-disable-next-line lingui/no-unlocalized-strings -- CSS selector, not UI copy
      '.page-header-desktop',
    );
    if (!containerEl || !headerEl) return;

    const measure = () => {
      const containerRect = containerEl.getBoundingClientRect();
      const headerRect = headerEl.getBoundingClientRect();
      const paddingLeft = parseFloat(getComputedStyle(headerEl).paddingLeft) || 0;
      // right-[-10px] pushes the wrapper's right edge 10px past the container.
      const rightEdge = containerRect.right + 10;
      const headerContentLeft = headerRect.left + paddingLeft;
      setFilledWidth(Math.max(0, Math.round(rightEdge - headerContentLeft)));
    };

    measure();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(headerEl);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [fillHeaderWidth, isExpanded]);

  // Collapse the bar and drop focus once a submit navigates away. The
  // destination header (Home or the Search section) shows the resulting context,
  // so the expanded field has done its job.
  const collapse = () => {
    setIsExpanded(false);
    inputRef.current?.blur();
  };

  const handleSubmit = (e: FormEvent | ReactMouseEvent) => {
    e.preventDefault();

    if (!query.trim()) {
      // Navigate back home if the user clears the search query
      router.navigate({
        to: '/home',
      });
      collapse();
      return;
    }

    if (!isSubscriber) {
      setShowPremiumModal(true);
      return;
    }

    router.navigate({
      to: '/search',
      search: {
        q: query,
        site: '',
        author: '',
        sort: 'relevance' as const,
        page: 1,
      },
    });
    collapse();
  };

  const handleIconClick = () => {
    // Non-subscribers can't search, so surface the upsell as soon as they reach
    // for search rather than letting them type a query first.
    if (!isSubscriber) {
      setShowPremiumModal(true);
      return;
    }

    if (!isExpanded) {
      setIsExpanded(true);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLButtonElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    const isMovingWithinContainer = relatedTarget?.closest('[data-search-container]');

    // Any time focus leaves the search bar, collapse it — even if a query is
    // still typed. (Tabbing between the input and the icon keeps focus within
    // the container, so it stays open.)
    if (!isMovingWithinContainer && isExpanded) {
      setIsExpanded(false);
    }
  };

  return (
    <div
      className={clsx(
        'transition-all duration-100 ease-out',
        // Don't collapse the search bar if we have an active search
        isBatchMode && !isExpanded
          ? 'pointer-events-none -translate-x-[22px] opacity-0'
          : 'translate-x-0 opacity-100',
      )}
    >
      <div
        ref={containerRef}
        data-search-container
        className="relative flex items-center"
      >
        {/* Expanded Search Input - positioned so icon appears inside.
            Anchored to the right and grows leftward, so a wider expanded width
            pushes the left edge further past the icon. In fill-header mode the
            width is measured (filledWidth) instead of the fixed w-88 class. */}
        <div
          data-search-input-wrapper
          data-expanded={isExpanded}
          style={
            filledWidth != null && isExpanded ? { width: `${filledWidth}px` } : undefined
          }
          className={clsx(
            'absolute right-[-10px] flex items-center overflow-hidden transition-all duration-200 ease-out',
            isExpanded ? 'w-80 opacity-100' : 'w-0 opacity-0',
          )}
        >
          <form onSubmit={handleSubmit} className="w-full">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onBlur={handleBlur}
                placeholder={t`Search articles, tags, or content`}
                className="form-input bg-surface-secondary h-8 w-full py-1 pr-12 pl-3 text-sm placeholder-gray-500"
                data-testid="search-input"
                tabIndex={isExpanded ? 0 : -1}
                // Don't allow changing the search query if we have bookmark selections,
                // since it can be confusing to find them again to unselect
                disabled={isBatchMode}
              />
            </div>
          </form>
        </div>

        {/* Search Icon Button - always visible, acts as submit when expanded */}
        <button
          type="button"
          onClick={isExpanded ? handleSubmit : handleIconClick}
          onBlur={handleBlur}
          className={clsx(
            'button-icon z-medium relative transition-all',
            isExpanded && 'p-0 hover:bg-transparent',
          )}
          aria-label={t`Search`}
          disabled={isBatchMode}
        >
          <Tooltip
            content={
              <ButtonIconTooltipContent
                label={t`Search`}
                // The "/" shortcut opens search; when already expanded the button
                // submits, so drop the hint.
                keys={isExpanded ? [] : ['/']}
              />
            }
          >
            <Icon name="search" />
          </Tooltip>
        </button>
      </div>

      {/* Premium Modal */}
      <InstapaperPremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        message={t`Full-text search is available with Instapaper Premium.`}
      />
    </div>
  );
}

export default CollapsibleSearchBar;
